import { ThunkDispatch } from "core-integration/src/store";
import { getFinishedEvents } from "core-integration/src/store/events/reducers";
import { getWinnersByEvent } from "core-integration/src/store/winners/reducers";
import { fetchEventWinners } from "core-integration/src/store/winners/thunks";
import Link from "plos/src/components/links/Link";
import Subheading from "plos/src/components/Subheading";
import { SurfaceContainer } from "plos/src/components/SurfaceContainer";
import { Cell, Column, Row, Table } from "plos/src/components/Table";
import { contentMain } from "plos/src/layouts";
import { fixedTable, footnote } from "plos/src/styles/utils.css";
import { useEffect, useState } from "react";
import { TableBody, TableHeader } from "react-aria-components";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isBlankWeek } from "../../../utils/events";
import { GameweekFilter } from "../WinnersFilters";
import {
  managerName,
  winnersPointsCell,
  winnersPointsColumn,
  winnersRankCell,
  winnersRankColumn,
  winnersTeamColumn,
} from "./winnersTables.css";

const GameweekTable = () => {
  const { t } = useTranslation("prizes");
  const dispatch = useDispatch<ThunkDispatch>();
  const finishedEvents = useSelector(getFinishedEvents);
  const eventWinners = useSelector(getWinnersByEvent);

  const [prizeEvent, setPrizeEvent] = useState(
    finishedEvents.length ? finishedEvents[finishedEvents.length - 1].id : 0,
  );

  useEffect(() => {
    if (prizeEvent) {
      dispatch(fetchEventWinners(prizeEvent));
    }
  }, [dispatch, prizeEvent]);

  const handlePrizeEventChange = (eventId: number) => setPrizeEvent(eventId);
  const eventWinnersList = eventWinners[prizeEvent] || [];

  return (
    <SurfaceContainer>
      <div className={contentMain}>
        <Subheading>
          {t("winnersTable.gameweekWinners", "Gameweek Winners")}
        </Subheading>
        {finishedEvents.length ? (
          <GameweekFilter
            finishedEvents={finishedEvents}
            prizeEvent={prizeEvent}
            handlePrizeEventChange={handlePrizeEventChange}
          />
        ) : (
          <span>
            {t("winnersTable.noCompletedGameweeks", "No Completed Gameweeks")}
          </span>
        )}
        <Table
          className={fixedTable}
          aria-label={
            t("winnersTable.gameweekWinners", "Gameweek Winners") as string
          }
        >
          <TableHeader>
            <Column isRowHeader className={winnersRankColumn}>
              {t("winnersTable.rank", "Rank")}
            </Column>
            <Column className={winnersTeamColumn}>
              {t("winnersTable.teamAndManager", "Team & Manager")}
            </Column>
            <Column className={winnersPointsColumn}>
              {t("winnersTable.gameweekPoints", "GW Pts")}
            </Column>
          </TableHeader>
          <TableBody>
            {!isBlankWeek(prizeEvent) ? (
              eventWinnersList.map((w) => (
                <Row key={w.entry_id}>
                  <Cell className={winnersRankCell}>{w.rank_sort}</Cell>
                  <Cell>
                    <Link to={`/entry/${w.entry_id}/event/${prizeEvent}`}>
                      <div>
                        <strong>{w.team_name}</strong>
                        <br />
                        <span className={managerName}>
                          {w.first_name} {w.last_name}
                        </span>
                      </div>
                    </Link>
                  </Cell>
                  <Cell className={winnersPointsCell}>{w.points}</Cell>
                </Row>
              ))
            ) : (
              <Row>
                <Cell colSpan={3}>
                  {t("winnersTable.noWinners", "No winners")}
                </Cell>
              </Row>
            )}
          </TableBody>
        </Table>
        <p className={footnote}>
          <Trans ns="prizes" i18nKey="winnersTable.eligibilityNote">
            <sup>*</sup> To be eligible to win a weekly prize, you must have
            joined the game before the start of the season or a minimum of two
            clear Gameweeks before the one in which you are top of the weekly
            leaderboard. Players who have played a chip that affects a Gameweek
            result (including Bench Boost, Free Hit, Triple Captain, Wildcard or
            Assistant Manager) will not be eligible to win a weekly prize for
            the Gameweek that such a chip has been used.
          </Trans>
        </p>
      </div>
    </SurfaceContainer>
  );
};

export default GameweekTable;
